import { UserRoundCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useAssignmentRequests, useRequestAssignment, useSprints } from '../../sprintApi'
import type { Project, Task } from '../../types'
import { toastError, useStaffLookup } from '../../ui'
import { AssignmentRequestList } from './AssignmentRequestList'

/** Görevin atama talepleri ve "ek görev / atama talebi" açma. */
export function TaskAssignmentPanel({ project, task, canWrite }: { project: Project; task: Task; canWrite: boolean }) {
  const { t } = useTranslation()
  const q = useAssignmentRequests({ taskId: task.id })
  const [open, setOpen] = useState(false)
  const items = q.data ?? []
  const pending = items.some((r) => r.status === 'pending')
  if (!items.length && !canWrite) return null
  return (
    <section className="rounded-xl border bg-card shadow-card">
      <header className="flex items-center gap-2 border-b px-4 py-3">
        <UserRoundCheck className="size-4 text-muted-foreground" aria-hidden />
        <h2 className="flex-1 font-semibold">{t('projects.assignments.title')}</h2>
        {canWrite && !pending ? (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            {t('projects.assignments.request')}
          </Button>
        ) : null}
      </header>
      <div className="p-4">
        {items.length ? <AssignmentRequestList items={items} /> : <p className="text-sm text-muted-foreground">{t('projects.assignments.none')}</p>}
      </div>
      {open ? <RequestModal project={project} task={task} onClose={() => setOpen(false)} /> : null}
    </section>
  )
}

function RequestModal({ project, task, onClose }: { project: Project; task: Task; onClose: () => void }) {
  const { t } = useTranslation()
  const { nameOf } = useStaffLookup()
  const sprints = useSprints(project.id)
  const req = useRequestAssignment()
  const [assigneeId, setAssigneeId] = useState('')
  const [sprintId, setSprintId] = useState('')
  const [reason, setReason] = useState('')
  return (
    <Modal
      open
      size="md"
      onOpenChange={(o) => !o && onClose()}
      icon={<UserRoundCheck />}
      title={t('projects.assignments.requestTitle', { key: task.key })}
      description={t('projects.assignments.requestHint')}
      footer={
        <>
          <Button variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!assigneeId || !reason.trim() || req.isPending}
            onClick={() =>
              req.mutate(
                { taskId: task.id, assigneeId, sprintId, reason },
                {
                  onSuccess: (r) => {
                    toast.success(t(r && (r as { id?: string }).id ? 'projects.assignments.pendingToast' : 'projects.assignments.assignedToast'))
                    onClose()
                  },
                  onError: toastError,
                },
              )
            }
          >
            {t('projects.assignments.send')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="ar-user">{t('projects.tasks.fields.assignee')}</Label>
          <Select
            id="ar-user"
            searchable
            value={assigneeId}
            onChange={setAssigneeId}
            options={[{ value: '', label: t('common.select') }, ...project.team.map((m) => ({ value: m.userId, label: nameOf(m.userId), description: m.title }))]}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ar-sprint">{t('projects.tasks.fields.sprint')}</Label>
          <Select
            id="ar-sprint"
            value={sprintId}
            onChange={setSprintId}
            options={[
              { value: '', label: t('projects.assignments.keepSprint') },
              ...(sprints.data ?? []).filter((x) => x.status !== 'closed').map((x) => ({ value: x.id, label: x.name, description: t(`projects.sprints.status.${x.status}`) })),
            ]}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ar-reason">{t('projects.assignments.reason')}</Label>
          <Textarea id="ar-reason" rows={3} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('projects.assignments.reasonHint')} />
        </div>
      </div>
    </Modal>
  )
}
